import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Panel, Stat } from "@/components/Panel";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Coffee, RefreshCw, Play, Square as StopIcon, Trash2, Wifi, WifiOff,
  CreditCard, Banknote, Receipt, TrendingUp, ArrowRight, Sparkles, Brain,
  TrendingDown, Target, Smartphone,
} from "lucide-react";
import POSLiveMobile from "@/pages/POSLiveMobile";
import POSLiveMicro from "@/pages/POSLiveMicro";

// Phase 36.18 — DARA POS Live dashboard.
// Shows today's revenue ticker, hourly txn chart, payment-mix pie, top SKUs,
// and a live transaction tail. Auto-refreshes every 30s, instantly when a
// demo run is started/stopped.

const REFRESH_MS = 30000;

const PAYMENT_LABELS = {
  card_tap: "Card · Tap",
  card_chip: "Card · Chip",
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  cash: "Cash",
};
const PAYMENT_COLORS = ["#FFB000", "#00E676", "#00BFFF", "#FF6B47", "#A78BFA"];

function fmtMoney(cents) {
  if (cents == null) return "—";
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export default function POSLive() {
  const [conn, setConn] = useState(null);
  const [today, setToday] = useState(null);
  const [hourly, setHourly] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [playbook, setPlaybook] = useState(null);
  const [refreshingAI, setRefreshingAI] = useState(false);
  // Phase 25.14a — workspace tier ("enterprise" | "micro"). When "micro" we
  // render the glance-first SMB view (POSLiveMicro) regardless of viewport.
  const [tier, setTier] = useState(null);
  const [wsIndustry, setWsIndustry] = useState("");
  const [microOverride, setMicroOverride] = useState(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("dara_pos_micro_override"); // "off" to force full
  });
  useEffect(() => {
    api.get("/workspace")
      .then(r => {
        setTier(r.data?.tier || "enterprise");
        setWsIndustry((r.data?.industry || "").toLowerCase());
      })
      .catch(() => setTier("enterprise"));
  }, []);
  const microUnits = useMemo(() => {
    // Industry-aware vocabulary for the hero row. Falls back to "txns".
    const i = wsIndustry;
    if (i.includes("barber") || i.includes("salon") || i.includes("hair")) return { plural: "cuts", singular: "cut" };
    if (i.includes("restaurant") || i.includes("café") || i.includes("cafe") || i.includes("food")) return { plural: "orders", singular: "order" };
    if (i.includes("retail")) return { plural: "sales", singular: "sale" };
    return { plural: "txns", singular: "txn" };
  }, [wsIndustry]);
  // Phase 25.4 — mobile-optimized variant for SMB owners checking takings on phone.
  // Auto-detect mobile width; allow manual override via toggle.
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === "undefined") return "auto";
    return localStorage.getItem("dara_pos_view") || "auto";
  });
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const useMobile = viewMode === "mobile" || (viewMode === "auto" && isNarrow);
  const setMode = (m) => {
    setViewMode(m);
    if (typeof localStorage !== "undefined") localStorage.setItem("dara_pos_view", m);
  };
  const intervalRef = useRef(null);

  const load = async () => {
    try {
      const [c, t, h, r] = await Promise.all([
        api.get("/pos/connections"),
        api.get("/pos/today"),
        api.get("/pos/hourly?days=1"),
        api.get("/pos/recent?limit=15"),
      ]);
      setConn(c.data);
      setToday(t.data);
      setHourly(h.data.hourly || []);
      setRecent(r.data.items || []);
      // Playbook fetched lazily — only if there's any data
      if ((t.data.txn_count || 0) > 0) {
        try {
          const p = await api.get("/pos/playbook");
          setPlaybook(p.data);
        } catch (_) { /* non-fatal */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load POS data");
    } finally {
      setLoading(false);
    }
  };

  const refreshAI = async () => {
    setRefreshingAI(true);
    try {
      const r = await api.post("/pos/playbook/refresh");
      setPlaybook(r.data);
      toast.success("DARA briefing refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshingAI(false);
    }
  };

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, REFRESH_MS);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, []);

  const startDemo = async () => {
    setBusy(true);
    try {
      const r = await api.post("/pos/demo/start");
      toast.success(r.data.message);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start demo");
    } finally {
      setBusy(false);
    }
  };

  const stopDemo = async () => {
    setBusy(true);
    try {
      await api.post("/pos/demo/stop");
      toast.success("Demo stream stopped");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to stop demo");
    } finally {
      setBusy(false);
    }
  };

  const clearDemo = async () => {
    if (!confirm("Wipe all demo POS records? This cannot be undone.")) return;
    setBusy(true);
    try {
      const r = await api.post("/pos/demo/clear");
      toast.success(`Cleared ${r.data.deleted} demo records`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear");
    } finally {
      setBusy(false);
    }
  };

  const chartData = useMemo(() => hourly.map(h => ({
    hour: (h.hour || "").slice(11, 13) + ":00",
    txns: h.txn_count,
    revenue: Math.round((h.revenue_cents || 0) / 100),
  })), [hourly]);

  const pieData = useMemo(() => (today?.payment_mix || []).map(p => ({
    name: PAYMENT_LABELS[p.payment_type] || p.payment_type,
    value: p.total_cents,
    sharePct: p.share_pct,
  })), [today]);

  const streaming = conn?.streaming;
  const hasAnyData = (today?.txn_count || 0) > 0 || recent.length > 0;
  const provider = conn?.connections?.[0];
  const allConnections = conn?.connections || [];
  const hasDemo = allConnections.some(c => c.is_demo && c.provider === "demo_coffee_shop");
  const hasSquare = allConnections.some(c => c.provider === "square" && (c.status === "active" || c.status === "live"));
  const sourceLabel = (() => {
    if (hasDemo && hasSquare) return "DEMO + SQUARE";
    if (hasSquare) return "SQUARE LIVE";
    if (hasDemo) return "DEMO ONLY";
    return null;
  })();

  // Phase 25.14a — micro SMB mode (tag-driven). Renders glance-first view.
  // User can temporarily "exit" to the full terminal via localStorage flag.
  if (tier === "micro" && microOverride !== "off") {
    return (
      <POSLiveMicro
        today={today}
        hourly={hourly}
        recent={recent}
        playbook={playbook}
        loading={loading}
        hasAnyData={hasAnyData}
        streaming={streaming}
        provider={provider}
        refreshingAI={refreshingAI}
        busy={busy}
        onRefresh={load}
        onRefreshAI={refreshAI}
        onStartDemo={startDemo}
        onStopDemo={stopDemo}
        onClearDemo={clearDemo}
        unitLabel={microUnits.plural}
        unitSingular={microUnits.singular}
        onExitMicro={() => {
          setMicroOverride("off");
          if (typeof localStorage !== "undefined") localStorage.setItem("dara_pos_micro_override", "off");
        }}
      />
    );
  }

  // Mobile variant — renders all the same data in a tab-based phone-friendly UI.
  if (useMobile) {
    return (
      <POSLiveMobile
        conn={conn}
        today={today}
        hourly={hourly}
        recent={recent}
        playbook={playbook}
        loading={loading}
        hasAnyData={hasAnyData}
        streaming={streaming}
        sourceLabel={sourceLabel}
        provider={provider}
        refreshingAI={refreshingAI}
        busy={busy}
        onRefresh={load}
        onRefreshAI={refreshAI}
        onStartDemo={startDemo}
        onStopDemo={stopDemo}
        onClearDemo={clearDemo}
        onViewSwitch={!isNarrow ? () => setMode("desktop") : null}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="pos-live-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            // POS LIVE · REAL-TIME TRANSACTION TELEMETRY
          </div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-1 flex items-center gap-3 flex-wrap">
            POS Live
            {streaming && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676] font-mono text-[10px] uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" />
                LIVE
              </span>
            )}
            {sourceLabel && (
              <span
                data-testid="pos-source-pill"
                className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-zinc-800 bg-zinc-950 text-zinc-300 font-mono text-[10px] uppercase tracking-widest"
                title="Where today's transaction data is coming from"
              >
                {sourceLabel}
              </span>
            )}
          </h1>
          <p className="text-xs text-zinc-500 max-w-xl mt-1.5">
            Real-time SMB POS dashboard — coffee shops, retailers, restaurants. Today's revenue, hourly volume,
            payment mix, top SKUs. {provider?.is_demo ? "Showing synthetic demo data." : "Live feed from your POS provider."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {tier === "micro" && microOverride === "off" && (
            <button
              onClick={() => {
                setMicroOverride(null);
                if (typeof localStorage !== "undefined") localStorage.removeItem("dara_pos_micro_override");
              }}
              data-testid="pos-back-to-glance"
              className="border border-[#FFB000]/40 bg-[#FFB000]/10 hover:bg-[#FFB000]/20 text-[#FFB000] px-3 py-2 font-mono text-[11px] uppercase tracking-widest flex items-center gap-2"
              title="Back to the simple glance view"
            >
              <Smartphone className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">GLANCE VIEW</span>
            </button>
          )}
          <button
            onClick={() => setMode("mobile")}
            data-testid="pos-mobile-view-toggle"
            className="border border-zinc-800 hover:border-[#FFB000] hover:text-[#FFB000] px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-2"
            title="Switch to mobile view"
          >
            <Smartphone className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">MOBILE</span>
          </button>
          <button
            onClick={load}
            disabled={loading}
            data-testid="pos-refresh"
            className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300 flex items-center gap-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          {!streaming && (
            <button
              onClick={startDemo}
              disabled={busy}
              data-testid="pos-demo-start"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:opacity-90 disabled:opacity-50"
            >
              <Play className="w-3.5 h-3.5" />
              {hasAnyData ? "RESUME DEMO" : "START COFFEE SHOP DEMO"}
            </button>
          )}
          {streaming && (
            <button
              onClick={stopDemo}
              disabled={busy}
              data-testid="pos-demo-stop"
              title="Pause synthetic coffee shop demo (real Square txns continue if connected)"
              className="inline-flex items-center gap-2 border border-[#FF6B47]/40 text-[#FF6B47] hover:bg-[#FF6B47]/10 font-mono text-[11px] uppercase tracking-widest px-4 py-2"
            >
              <StopIcon className="w-3.5 h-3.5" />
              PAUSE DEMO
            </button>
          )}
          {hasAnyData && provider?.is_demo && (
            <button
              onClick={clearDemo}
              disabled={busy}
              data-testid="pos-demo-clear"
              className="border border-zinc-800 hover:border-zinc-700 hover:text-[#FF3B30] px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-2"
              title="Wipe all demo records"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Empty state — no demo started yet */}
      {!loading && !hasAnyData && (
        <Panel title="// COFFEE SHOP MVP · DEMO MODE" testId="pos-empty-state">
          <div className="p-10 text-center">
            <Coffee className="w-12 h-12 text-[#FFB000] mx-auto mb-4" />
            <h3 className="font-heading font-black text-2xl tracking-tight">Try DARA POS Live</h3>
            <p className="text-sm text-zinc-400 mt-3 max-w-md mx-auto leading-relaxed">
              See exactly what a coffee shop owner gets when they connect their POS terminal — live transaction
              feed, hourly revenue chart, payment mix, top-selling SKUs. Click below to start a synthetic
              coffee shop demo (realistic morning rush, lunch lull, afternoon dip).
            </p>
            <button
              onClick={startDemo}
              disabled={busy}
              data-testid="pos-empty-start"
              className="mt-6 inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 hover:opacity-90 disabled:opacity-50"
            >
              <Play className="w-3.5 h-3.5" />
              START COFFEE SHOP DEMO
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <div className="font-mono text-[10px] text-zinc-700 mt-5 max-w-md mx-auto leading-relaxed">
              <span className="text-[#00E676]">✓</span> Live Square POS connected. Shopify POS / Stripe Terminal coming next.
            </div>
          </div>
        </Panel>
      )}

      {/* KPI strip */}
      {hasAnyData && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900">
          <Stat testId="pos-revenue" label="REVENUE TODAY" value={fmtMoney(today?.revenue_cents)} accent="green" />
          <Stat testId="pos-txns" label="TRANSACTIONS" value={today?.txn_count ?? "—"} accent="amber" />
          <Stat testId="pos-avg-ticket" label="AVG TICKET" value={fmtMoney(today?.avg_ticket_cents)} accent="white" />
          <Stat testId="pos-tips" label="TIPS COLLECTED" value={fmtMoney(today?.tip_cents)} accent="amber" />
          <Stat testId="pos-tax" label="GST/TAX" value={fmtMoney(today?.tax_cents)} accent="white" />
        </div>
      )}

      {/* DARA Briefing panel — Phase 36.19 */}
      {hasAnyData && playbook && (
        <Panel
          title="// DARA BRIEFING · DARA-GROUNDED · WHY WAS TODAY DIFFERENT?"
          testId="pos-ai-briefing"
          right={
            <button
              onClick={refreshAI}
              disabled={refreshingAI}
              data-testid="pos-ai-refresh"
              title="Re-run DARA analysis with the latest minute"
              className="border border-[#FFB000]/40 hover:bg-[#FFB000]/10 text-[#FFB000] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
            >
              <Sparkles className={`w-3 h-3 ${refreshingAI ? "animate-pulse" : ""}`} />
              {refreshingAI ? "REFRESHING…" : "REFRESH AI"}
            </button>
          }
        >
          <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Close-of-day projection */}
            <div className="lg:col-span-1 border border-[#FFB000]/40 bg-[#FFB000]/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-[#FFB000]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] font-bold">CLOSE-OF-DAY PROJECTION</span>
              </div>
              {playbook.close_of_day?.projection_cents != null ? (
                <>
                  <div className="font-heading font-black text-3xl text-white tracking-tight">
                    {fmtMoney(playbook.close_of_day.projection_cents)}
                  </div>
                  <div className="font-mono text-[10px] mt-1 flex items-center gap-1.5">
                    {playbook.close_of_day.delta_vs_baseline_pct >= 0 ? (
                      <span className="flex items-center gap-1 text-[#00E676]">
                        <TrendingUp className="w-3 h-3" />
                        +{playbook.close_of_day.delta_vs_baseline_pct}% vs same-DOW avg
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[#FF6B47]">
                        <TrendingDown className="w-3 h-3" />
                        {playbook.close_of_day.delta_vs_baseline_pct}% vs same-DOW avg
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed">
                    Based on same day-of-week pace from last {playbook.close_of_day.history_days_used} weeks.
                    Same-DOW avg: {fmtMoney(playbook.close_of_day.baseline_avg_revenue_cents)}.
                  </div>
                </>
              ) : (
                <div className="font-mono text-[11px] text-zinc-400 leading-relaxed">
                  Insufficient history. DARA needs at least 2 prior same-DOW days of data — try again next {new Date().toLocaleDateString("en-US", { weekday: "long" })}.
                </div>
              )}
            </div>

            {/* DARA narrative */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-[#A78BFA]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#A78BFA] font-bold">DARA · POS COPILOT</span>
              </div>
              <div className="text-sm text-zinc-200 leading-relaxed whitespace-pre-line">
                {playbook.summary || "No summary yet."}
              </div>

              {(playbook.drivers || []).length > 0 && (
                <div className="pt-2 border-t border-zinc-900">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// KEY DRIVERS</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {playbook.drivers.map((d, i) => (
                      <div key={i} className="border border-zinc-900 bg-black p-2.5">
                        <div className="font-mono text-[10px] text-[#FFB000] uppercase tracking-widest mb-0.5">{d.label}</div>
                        <div className="font-mono text-[11px] text-zinc-400 leading-snug">{d.detail}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(playbook.actions || []).length > 0 && (
                <div className="pt-2 border-t border-zinc-900">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676] mb-2 font-bold">// RECOMMENDED ACTIONS</div>
                  <ul className="space-y-1.5">
                    {playbook.actions.map((a, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        <span className="text-[#00E676] flex-shrink-0 mt-0.5">→</span>
                        <span>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-[#00E676] mr-2">{a.label}</span>
                          <span className="text-zinc-300">{a.detail}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {playbook.updated_at && (
                <div className="font-mono text-[9px] text-zinc-700 pt-2">
                  Last updated · {fmtTime(playbook.updated_at)} · cached for 60min
                </div>
              )}
            </div>
          </div>
        </Panel>
      )}

      {hasAnyData && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <Panel title="// HOURLY REVENUE · TODAY" testId="pos-hourly-chart">
              <div className="p-4 h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontFamily: "monospace", fontSize: 10, fill: "#71717a" }} />
                    <YAxis yAxisId="left" tick={{ fontFamily: "monospace", fontSize: 10, fill: "#71717a" }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontFamily: "monospace", fontSize: 10, fill: "#71717a" }} />
                    <Tooltip
                      contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", fontFamily: "monospace", fontSize: 11 }}
                      cursor={{ fill: "rgba(255,176,0,0.05)" }}
                    />
                    <Bar yAxisId="left" dataKey="revenue" fill="#FFB000" name="Revenue ($)" />
                    <Bar yAxisId="right" dataKey="txns" fill="#00E676" name="Txn Count" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>
          <Panel title="// PAYMENT MIX" testId="pos-payment-mix">
            <div className="p-4 h-[280px]">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} stroke="#000">
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PAYMENT_COLORS[i % PAYMENT_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", fontFamily: "monospace", fontSize: 11 }}
                      formatter={(v, n, p) => [`${fmtMoney(v)} (${p.payload.sharePct}%)`, n]}
                    />
                    <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 10, color: "#a1a1aa" }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center font-mono text-xs text-zinc-600 py-12">No payments yet today.</div>
              )}
            </div>
          </Panel>
        </div>
      )}

      {hasAnyData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Panel title="// TOP SKUs · TODAY" testId="pos-top-skus">
            <div className="divide-y divide-zinc-900">
              {(today?.top_skus || []).map((s) => (
                <div key={s.sku} className="px-4 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-white">{s.name}</div>
                    <div className="font-mono text-[10px] text-zinc-600 mt-0.5">SKU · {s.sku}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-mono text-xs text-[#FFB000]">{fmtMoney(s.total_cents)}</div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5">× {s.qty}</div>
                  </div>
                </div>
              ))}
              {(today?.top_skus || []).length === 0 && (
                <div className="p-8 text-center font-mono text-xs text-zinc-600">No SKU data yet.</div>
              )}
            </div>
          </Panel>

          <Panel title="// LIVE TRANSACTION TAIL" testId="pos-recent-tail">
            <div className="divide-y divide-zinc-900 max-h-[340px] overflow-y-auto">
              {recent.map(t => (
                <div key={t.id} className="px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-zinc-950/40">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-2">
                      {t.payment_type === "cash" ? <Banknote className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                      {fmtTime(t.occurred_at)} · {PAYMENT_LABELS[t.payment_type] || t.payment_type}
                    </div>
                    <div className="font-mono text-[10px] text-zinc-600 mt-0.5 truncate">
                      {(t.items || []).map(i => `${i.qty}× ${i.name}`).join(" · ") || "—"}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-mono text-xs text-white font-bold">{fmtMoney(t.total_cents)}</div>
                    {t.tip_cents > 0 && (
                      <div className="font-mono text-[9px] text-[#00E676]">+ {fmtMoney(t.tip_cents)} tip</div>
                    )}
                  </div>
                </div>
              ))}
              {recent.length === 0 && (
                <div className="p-8 text-center font-mono text-xs text-zinc-600">No transactions yet.</div>
              )}
            </div>
          </Panel>
        </div>
      )}

      {hasAnyData && (
        <div className="font-mono text-[10px] text-zinc-700 text-center pt-2">
          {streaming ? "🟢 Live feed · auto-refresh every 30s" : "⏸ Demo paused · last update " + fmtTime(today?.as_of)}
          {provider?.is_demo && " · DEMO DATA"}
        </div>
      )}
    </div>
  );
}
